import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface SpecContextValue {
  activeSpec: any;
  selectSpec: (spec: any) => void;
  clearSpec: () => void;
  hasActiveSpec: boolean;
  isOpenAPI: boolean;
  isPostman: boolean;
}

interface SpecProviderProps {
  children: React.ReactNode;
}

const SpecContext = createContext<SpecContextValue | undefined>(undefined);

export const useSpec = (): SpecContextValue => {
  const context = useContext(SpecContext);
  if (!context) {
    throw new Error('useSpec must be used within a SpecProvider');
  }
  return context;
};

export const SpecProvider: React.FC<SpecProviderProps> = ({ children }) => {
  const [activeSpec, setActiveSpec] = useState<any>(null);

  // Load active spec from localStorage on mount
  useEffect(() => {
    const savedSpec = localStorage.getItem('activeSpec');
    if (savedSpec) {
      try {
        setActiveSpec(JSON.parse(savedSpec));
      } catch (error) {
        console.error('Failed to load active spec from localStorage:', error);
        localStorage.removeItem('activeSpec');
      }
    }
  }, []);

  // Save active spec to localStorage when it changes
  useEffect(() => {
    if (activeSpec) {
      localStorage.setItem('activeSpec', JSON.stringify(activeSpec));
    } else {
      localStorage.removeItem('activeSpec');
    }
  }, [activeSpec]);

  const selectSpec = useCallback((spec: any): void => {
    setActiveSpec(spec);
  }, []);

  const clearSpec = useCallback((): void => {
    setActiveSpec(null);
  }, []);

  const isOpenAPI = activeSpec?.specCategory === 'openapi' || (!activeSpec?.specCategory && !!activeSpec);
  const isPostman = activeSpec?.specCategory === 'postman';

  const value = useMemo<SpecContextValue>(
    () => ({
      activeSpec,
      selectSpec,
      clearSpec,
      hasActiveSpec: !!activeSpec,
      isOpenAPI,
      isPostman,
    }),
    [activeSpec, selectSpec, clearSpec, isOpenAPI, isPostman],
  );

  return <SpecContext.Provider value={value}>{children}</SpecContext.Provider>;
};
